import {
  menu,
  type MenuConfig,
} from '@blocksuite/affine-components/context-menu';
import { html } from 'lit/static-html.js';

import { type DataViewI18nKey, dataViewI18nT } from '../i18n.js';
import { renderUniLit } from '../utils/uni-component/index.js';
import type { Property } from '../view-manager/property.js';

// StreamLain hook surface — also consumed by the table-header type picker.
// Kept minimal here so the data-view package stays free of frontend deps.
interface StreamlainColumnMenuHook {
  name: string;
  getSubMenu?: (column: Property, tableViewManager: unknown) => MenuConfig;
  onAfterTypeChange?: (
    column: unknown,
    tableViewManager: unknown,
    newType: string,
    anchor: HTMLElement
  ) => void;
}
interface WindowWithStreamlainHooks extends Window {
  __streamlainColumnMenuHooks?: StreamlainColumnMenuHook[];
}

function getStreamlainColumnMenuHooks() {
  return typeof window !== 'undefined'
    ? (window as WindowWithStreamlainHooks).__streamlainColumnMenuHooks
    : undefined;
}

const DATA_VIEW_MENU_ENTRY_CLASS = { 'data-view-menu-entry': true } as const;

const PROPERTY_TYPE_I18N_KEYS: Record<string, DataViewI18nKey> = {
  attachment: 'property.type.attachment',
  checkbox: 'property.type.checkbox',
  'created-by': 'property.type.created-by',
  date: 'property.type.date',
  'doc-reference': 'property.type.doc-reference',
  image: 'property.type.image',
  member: 'property.type.member',
  'multi-select': 'property.type.multi-select',
  number: 'property.type.number',
  progress: 'property.type.progress',
  relation: 'property.type.relation',
  rollup: 'property.type.rollup',
  select: 'property.type.select',
  text: 'property.type.text',
  title: 'property.type.title',
  'updated-by': 'property.type.updated-by',
};

export function propertyTypeLabel(type: string, fallback: string): string {
  const key = PROPERTY_TYPE_I18N_KEYS[type];
  return key ? dataViewI18nT(key) : fallback;
}

export function propertyDeleteFallbackName(): string {
  return dataViewI18nT('property.deleteFallbackName');
}

export function propertyDeleteConfirmMessage(name: string): string {
  return dataViewI18nT('property.deleteConfirm', { name });
}

export function streamlainColumnMenuConfigs(property: Property): MenuConfig[] {
  return (
    getStreamlainColumnMenuHooks()
      ?.map(hook => hook.getSubMenu?.(property, property.view))
      .filter((config): config is MenuConfig => !!config) ?? []
  );
}

export function notifyStreamlainColumnTypeChanged(
  property: Property,
  newType: string,
  anchor: HTMLElement
): void {
  getStreamlainColumnMenuHooks()?.forEach(hook => {
    hook.onAfterTypeChange?.(property, property.view, newType, anchor);
  });
}

export const inputConfig = (property: Property) => {
  return menu.input({
    prefix: html`
      <div class="affine-database-column-type-menu-icon">
        ${renderUniLit(property.icon)}
      </div>
    `,
    initialValue: property.name$.value,
    placeholder: dataViewI18nT('property.namePlaceholder'),
    onBlur: text => {
      property.nameSet(text);
    },
  });
};
export const typeConfig = (property: Property) => {
  return menu.group({
    items: [
      menu.subMenu({
        name: dataViewI18nT('property.type'),
        class: 'data-view-menu-entry',
        hide: () => !property.typeCanSet,
        // Keep the column-header menu open after type change so StreamLain
        // hooks (onAfterTypeChange) can synchronously auto-expand a follow-up
        // submenu (e.g. Relation Config) inside the still-open main menu.
        closeOnSelect: false,
        postfix: html` <div
          class="affine-database-column-type-icon"
          style="color: var(--streamlain-text-secondary-color);gap:4px;font-size: 14px;"
        >
          ${renderUniLit(property.icon)}
          ${(() => {
            const currentMeta = property.view.propertyMetas$.value.find(
              v => v.type === property.type$.value
            );
            return currentMeta
              ? propertyTypeLabel(currentMeta.type, currentMeta.config.name)
              : '';
          })()}
        </div>`,
        options: {
          title: {
            text: dataViewI18nT('property.typeTitle'),
          },
          items: [
            menu.group({
              items: property.view.propertyMetas$.value.map(config => {
                return menu.action({
                  isSelected: config.type === property.type$.value,
                  name: propertyTypeLabel(config.type, config.config.name),
                  prefix: renderUniLit(config.renderer.icon),
                  class: DATA_VIEW_MENU_ENTRY_CLASS,
                  select: () => {
                    if (property.type$.value === config.type) {
                      return;
                    }
                    property.typeSet?.(config.type);
                    // Anchor fallback: the open type-submenu popover lives in
                    // an <affine-menu>. Use the outermost one (the original
                    // column-header menu) so the follow-up popup sits at the
                    // column, not somewhere random. document.body would
                    // render in viewport corner.
                    const openMenus = document.querySelectorAll('affine-menu');
                    const anchor =
                      (openMenus[0] as HTMLElement | undefined) ??
                      document.body;
                    notifyStreamlainColumnTypeChanged(
                      property,
                      config.type,
                      anchor
                    );
                  },
                });
              }),
            }),
          ],
        },
      }),
    ],
  });
};
